// src/pages/buyer/OrdersPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, Package, ShoppingBag, TrendingUp, Truck,
  CheckCircle, Clock, XCircle, ChevronRight,
} from 'lucide-react'
import { ordersApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardBody, StatusBadge, PageSpinner, Pagination, Empty, Button } from '@/components/common/ui'

const STATUS_TABS = [
  { key: '',                                      label: 'All',       icon: ShoppingBag },
  { key: 'confirmed,processing,shipped,out_for_delivery', label: 'Active',    icon: Clock },
  { key: 'shipped,out_for_delivery',              label: 'In Transit', icon: Truck },
  { key: 'delivered',                             label: 'Delivered',  icon: CheckCircle },
  { key: 'cancelled',                             label: 'Cancelled',  icon: XCircle },
]

export default function OrdersPage() {
  const [tab, setTab] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { format } = useCurrency()

  const { data, isLoading } = useQuery({
    queryKey: ['buyer-orders', tab, search, page],
    queryFn: () => ordersApi.list({ status: tab || undefined, search: search || undefined, page }),
  })

  const payload  = data?.data?.data || {}
  const orders   = payload?.orders?.data || []
  const counts   = payload?.counts || {}
  const kpis     = payload?.kpis || {}
  const lastPage = payload?.orders?.last_page || 1
  const totalAll = counts.all || 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2">
          <Package size={20} className="text-pink" /> My Orders
        </h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-pink" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Total Spend</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{format(kpis.total_spend || 0)}</p>
          <p className="text-[10px] text-muted">Lifetime</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag size={14} className="text-blue-500" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">This Month</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{format(kpis.spend_this_month || 0)}</p>
          <p className="text-[10px] text-muted">{kpis.orders_this_month || 0} orders</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-amber-500" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Active</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{counts.active || 0}</p>
          <p className="text-[10px] text-muted">In progress</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Truck size={14} className="text-indigo-500" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">In Transit</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{kpis.awaiting_delivery || 0}</p>
          <p className="text-[10px] text-muted">Awaiting delivery</p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="bg-white border border-border rounded-2xl p-3 mb-5">
        <div className="flex items-center gap-1 overflow-x-auto">
          {STATUS_TABS.map(t => {
            const count = t.key === ''
              ? totalAll
              : t.key.includes(',')
                ? t.key.split(',').reduce((s, k) => s + (counts[k] || 0), 0)
                : (counts[t.key] || 0)
            const Icon = t.icon
            const isActive = tab === t.key
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setPage(1) }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  isActive ? 'bg-pink text-white' : 'text-muted hover:bg-surface'
                }`}>
                <Icon size={13} />
                {t.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                    isActive ? 'bg-white/20' : 'bg-surface'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="input pl-9 w-full" placeholder="Search by order number…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }} />
      </div>

      {/* Loading */}
      {isLoading && <PageSpinner />}

      {/* Empty */}
      {!isLoading && orders.length === 0 && (
        <Empty icon="📦" title="No orders found"
          description={tab ? 'No orders match this filter.' : 'Your orders will appear here once you make a purchase.'}
          action={!tab && <Link to="/fabrics"><Button>Start Shopping</Button></Link>}
        />
      )}

      {/* Order Cards */}
      <div className="space-y-3">
        {orders.map(order => {
          const isShipped = ['shipped', 'out_for_delivery'].includes(order.status)
          return (
            <Link key={order.id} to={`/buyer/orders/${order.id}`} className="block">
              <Card className="hover:shadow-card-md hover:border-pink/30 transition-all">
                <CardBody>
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="w-10 h-10 rounded-lg bg-pink/10 flex items-center justify-center flex-shrink-0">
                      <Package size={18} className="text-pink" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-sm text-gray-900">{order.order_number}</span>
                        <StatusBadge status={order.status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted">
                        <span>{order.supplier?.business_name || order.supplier?.name}</span>
                        <span>{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span>{order.items?.length ?? 0} item{(order.items?.length ?? 0) !== 1 ? 's' : ''}</span>
                      </div>

                      {/* Tracking inline */}
                      {isShipped && order.tracking_number && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-indigo-600">
                          <Truck size={12} />
                          <span>{order.courier_name} — <span className="font-mono font-bold">{order.tracking_number}</span></span>
                        </div>
                      )}

                      {/* Estimated delivery */}
                      {isShipped && order.estimated_delivery_date && (
                        <p className="text-[11px] text-muted mt-0.5">
                          Est. delivery: <strong>{new Date(order.estimated_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</strong>
                        </p>
                      )}
                    </div>

                    {/* Amount + Action */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-sm text-gray-900">{format(order.total_amount)}</p>
                      <span className="text-xs text-pink font-bold flex items-center gap-0.5 justify-end mt-1">
                        View <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          )
        })}
      </div>

      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
